// This device's settings: one JSON object under `desyr_settings` with kebab-case fields
// (90-test-hooks.md). A field set to its default is removed.
import type { ColourStyle } from '../design/look';
import { useSyncExternalStore } from 'react';
import { readJson, writeJson } from './storage';

export interface Settings {
  /** 01-design.md: colour mode; absent = system. */
  theme?: 'light' | 'dark';
  /** 01-design.md: accent seed as hex; absent = Indigo. */
  accent?: string;
  /** 01-design.md: 0.5 or 1; absent = Normal (0). */
  contrast?: number;
  /** 01-design.md: absent = Balanced. */
  'colour-style'?: ColourStyle;
  /** 01-design.md, The theme in use 5: another look's page, surface and rail colours for the head script; written by theme.ts. */
  'paint-cache'?: Record<'light' | 'dark', { page: string; surface: string; rail: string }>;
  /** 02-building-blocks.md, Languages: "en", "de"; absent = follow the browser. */
  language?: string;
  /** 21: no typing notices, read receipts sent privately. */
  'private-activity'?: boolean;
  /** 17: the member list beside a channel; absent = shown. */
  'members-panel'?: boolean;
  /** 21: times with 24 hours; absent = by the device's region (02). */
  'clock-24h'?: boolean;
  /** 21: a date pattern; absent = by language. */
  'date-format'?: string;
  /** 15: Enter makes a new line, Ctrl/⌘+Enter sends. */
  'enter-newline'?: boolean;
  /** 15: no send button. */
  'hide-send-button'?: boolean;
  /** 13: timeline lines that are hidden. */
  'hide-membership'?: boolean;
  /** 13: absent = hidden. */
  'hide-profile-changes'?: boolean;
  /** 13: absent = media load by themselves. */
  'auto-load-media'?: boolean;
  /** 13: absent = on. */
  'link-previews'?: boolean;
  'link-previews-encrypted'?: boolean;
  /** 12: absent = on. */
  'desktop-notifications'?: boolean;
  'notification-sound'?: boolean;
  /** 01: 12–20, absent = 14. */
  'message-size'?: number;
  /** 01: 0–5, absent = 3. */
  'group-spacing'?: number;
  /** 22: the call's voice processing; absent = on (the browser's). */
  'noise-suppression'?: boolean;
  'echo-cancellation'?: boolean;
  /** 29: this device's index of encrypted chats; absent = on. */
  'encrypted-search'?: boolean;
  /** 16: 0–5, absent = none. */
  'emoji-skin-tone'?: number;
}

/** The fields whose default isn't "absent = off". */
const DEFAULTS: Settings = {
  'hide-profile-changes': true,
  'auto-load-media': true,
  'link-previews': true,
  'desktop-notifications': true,
  'notification-sound': true,
  'members-panel': true,
  'noise-suppression': true,
  'echo-cancellation': true,
  'encrypted-search': true,
};

/** A setting with its default. */
export const setting = <K extends keyof Settings>(field: K): Settings[K] => memory[field] ?? DEFAULTS[field];

const KEY = 'desyr_settings';
const listeners = new Set<() => void>();
let memory: Settings = readJson<Settings>(KEY) ?? {};

export const readSettings = (): Settings => memory;

/** The clock and date format of Settings → General, for the time helpers. */
export const clock = () => ({ h24: memory['clock-24h'], dateFormat: memory['date-format'] });

/** Draws again when a setting changes. */
export function useSettings(): Settings {
  return useSyncExternalStore(onSettings, readSettings);
}

export function writeSetting<K extends keyof Settings>(field: K, value: Settings[K] | undefined): void {
  const next = { ...memory };
  if (value === undefined) delete next[field];
  else next[field] = value;
  memory = next;
  writeJson(KEY, next);
  listeners.forEach((l) => l());
}

export function onSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

// Another tab changed them.
window.addEventListener('storage', (e) => {
  if (e.key !== KEY && e.key !== null) return;
  memory = readJson<Settings>(KEY) ?? {};
  listeners.forEach((l) => l());
});
